import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';
import axiosInstance from '../axiosConfig';
import { toast } from 'react-toastify';
import theme from '../config/theme';
import '../config/pastelTheme.css';
import '../config/setupForm.css';

const apiUrl = (path) => `${window.location.origin}${path.startsWith('/') ? path : '/' + path}`;

const Setup = () => {
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSettings, setSavedSettings] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD'); // Moeda selecionada (padrão: USD)
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 0.93,
    GBP: 0.79,
    BRL: 5.20
  }); // Taxas de câmbio (valores iniciais aproximados)
  const [settings, setSettings] = useState({
    initial_amount: 10000,
    exchange_rate: 1.05,
    entry_fee: 0.001,
    exit_fee: 0.001,
    profit_rate: 0.008,
    daily_withdrawal_rate: 0.002,
    operations_per_day: 3,
    simulation_months: 24,
    working_days: 5 // Valor padrão: 5 dias úteis na semana
  });
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSettings, setSelectedSettings] = useState([]);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Buscar configuração por ID
  const fetchSettingById = useCallback(async (settingId) => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axiosInstance.get(apiUrl(`/api/investment/settings/${settingId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        // Convertendo todos os valores para garantir que sejam numéricos
        const initialAmount = parseFloat(response.data.initial_amount);
        const exchangeRate = parseFloat(response.data.exchange_rate);
        const entryFee = parseFloat(response.data.entry_fee);
        const exitFee = parseFloat(response.data.exit_fee);
        const profitRate = parseFloat(response.data.profit_rate);
        const dailyWithdrawalRate = parseFloat(response.data.daily_withdrawal_rate);
        const operationsPerDay = parseInt(response.data.operations_per_day);
        const simulationMonths = parseInt(response.data.simulation_months);
        const workingDays = parseInt(response.data.working_days);
        
        // Verificando valores antes de atualizar o estado
        const settingsData = {
          initial_amount: isNaN(initialAmount) ? 10000 : initialAmount,
          exchange_rate: isNaN(exchangeRate) ? 1.05 : exchangeRate,
          entry_fee: isNaN(entryFee) ? 0.001 : entryFee,
          exit_fee: isNaN(exitFee) ? 0.001 : exitFee,
          profit_rate: isNaN(profitRate) ? 0.008 : profitRate,
          daily_withdrawal_rate: isNaN(dailyWithdrawalRate) ? 0.002 : dailyWithdrawalRate,
          operations_per_day: isNaN(operationsPerDay) ? 3 : operationsPerDay,
          simulation_months: isNaN(simulationMonths) ? 24 : simulationMonths,
          working_days: isNaN(workingDays) ? 5 : workingDays
        };
        
        setSettings(settingsData);
        setActiveTab('new');
        toast.info('Configuração carregada com sucesso');
      } else {
        setFetchError('Configuração não encontrada');
        toast.error('Configuração não encontrada');
      }
    } catch (error) {
      setFetchError(error.message || 'Erro ao carregar configuração');
      toast.error('Erro ao carregar configuração. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axiosInstance.get(apiUrl('/api/investment/settings'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        // Convertendo todos os valores para garantir que sejam numéricos usando um loop mais eficiente
        const keys = ['initial_amount', 'exchange_rate', 'entry_fee', 'exit_fee', 'profit_rate', 'daily_withdrawal_rate', 'operations_per_day', 'simulation_months'];
        const defaults = [10000, 1.05, 0.001, 0.001, 0.008, 0.002, 3, 24];
        
        const settingsData = {};
        keys.forEach((key, index) => {
          const value = parseFloat(response.data[key]);
          settingsData[key] = isNaN(value) ? defaults[index] : value;
        });
        
        setSettings(settingsData);
      } else {
        setFetchError('Resposta do backend vazia ou inválida');
      }
    } catch (error) {
      // Utilizador novo sem settings: normal — mantém defaults do formulário
      if (error.response && error.response.status === 404) {
        setFetchError(null);
      } else if (error.response && error.response.status === 401) {
        setFetchError('Sessão expirada');
        toast.error('Sua sessão expirou. Por favor, faça login novamente.');
      } else {
        setFetchError(error.message || 'Erro ao carregar configurações');
        console.warn('fetchSettings:', error.response?.status, error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Buscar todas as configurações salvas
  const fetchAllSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        toast.error('Você precisa estar autenticado para ver as configurações.');
        return;
      }

      console.log('Buscando cenários salvos...');
      // Usando o endpoint correto em investment.js com parâmetro de cache-busting
      const response = await axiosInstance.get(apiUrl(`/api/investment/settings/all?_t=${new Date().getTime()}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('DADOS BRUTOS DO SERVER:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        // Garantir que os valores sejam convertidos para números
        const formattedSettings = response.data.map(setting => {
          console.log(`Processando configuração ID ${setting.id}, valor inicial: ${setting.initial_amount}, tipo: ${typeof setting.initial_amount}`);
          return {
            ...setting,
            // Garantir valores numéricos com parseFloat explícito
            initial_amount: parseFloat(setting.initial_amount || 0),
            exchange_rate: parseFloat(setting.exchange_rate || 1),
            entry_fee: parseFloat(setting.entry_fee || 0),
            exit_fee: parseFloat(setting.exit_fee || 0),
            profit_rate: parseFloat(setting.profit_rate || 0),
            daily_withdrawal_rate: parseFloat(setting.daily_withdrawal_rate || 0),
            operations_per_day: parseInt(setting.operations_per_day || 3),
            simulation_months: parseInt(setting.simulation_months || 24),
            working_days: parseInt(setting.working_days || 5)
          };
        });
        
        console.log('DADOS FORMATADOS:', formattedSettings);
        setSavedSettings(formattedSettings);
      } else {
        console.error('Formato de resposta inválido:', response.data);
        toast.error('Erro ao carregar cenários salvos: formato de resposta inválido');
        setSavedSettings([]);
      }
    } catch (error) {
      console.error('Erro ao buscar configurações salvas:', error);
      
      // Tratamento específico para erro 401 (Unauthorized)
      if (error.response && error.response.status === 401) {
        console.error('Erro de autenticação: token expirado ou inválido');
        toast.error('Sua sessão expirou. Por favor, faça login novamente.');
        
        // Limpar dados da sessão
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirecionar para login após pequeno delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (!error.response) {
        console.error('Sem resposta do servidor:', error.message);
        toast.error('Erro de conexão ao carregar cenários. Verifique a API.');
      } else {
        console.warn('fetchAllSettings status', error.response.status, error.response.data);
      }
      
      setSavedSettings([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    // Verificar se há um ID de configuração na URL
    const query = new URLSearchParams(location.search);
    const settingId = query.get('setting_id');
    
    if (settingId) {
      fetchSettingById(settingId);
    } else {
      fetchSettings();
    }
    
    // Buscar todas as configurações salvas
    fetchAllSettings();
  }, [location.search, fetchSettingById, fetchSettings, fetchAllSettings]);

  // Função para converter valor entre moedas
  const convertCurrency = (value, fromCurrency, toCurrency) => {
    // Converte para USD primeiro (moeda base)
    const valueInUSD = value / exchangeRates[fromCurrency];
    // Converte de USD para a moeda alvo
    return valueInUSD * exchangeRates[toCurrency];
  };

  // Função para formatar valor na moeda selecionada
  const formatCurrencyValue = (value, currency = selectedCurrency) => {
    const currencySymbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      BRL: 'R$'
    };
    
    // Arredonda para 2 casas decimais
    const formattedValue = parseFloat(value).toFixed(2);
    return `${currencySymbols[currency]}${formattedValue}`;
  };

  // Função para formatar entrada de moeda
  const formatCurrencyInput = (value) => {
    // Se não há valor, retornar string vazia
    if (!value) return '';
    
    // Se o valor estiver sendo editado (com cursor no meio), retornar apenas o valor sem formatação
    if (value.startsWith(currencySymbols[selectedCurrency]) && value.length > 1) {
      return value;
    }
    
    // Remover qualquer formatação existente para obter apenas o número
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Se não há valor numérico, retornar apenas o símbolo da moeda
    if (!numericValue) return currencySymbols[selectedCurrency];
    
    // Converter para número
    const numberValue = parseFloat(numericValue);
    if (isNaN(numberValue)) return currencySymbols[selectedCurrency];
    
    // Formatar com símbolo da moeda selecionada
    return `${currencySymbols[selectedCurrency]}${numberValue.toFixed(2)}`;
  };

  // Manipulador de entrada de valor monetário
  const handleCurrencyInputChange = (e) => {
    const { name, value } = e.target;
    
    // Permitir apenas dígitos e um único ponto decimal
    const validInput = /^[0-9]*\.?[0-9]*$/.test(value);
    
    if (validInput || value === '') {
      // Atualizar o state com o valor numérico
      setSettings(prevSettings => ({
        ...prevSettings,
        [name]: value === '' ? 0 : parseFloat(value) || 0
      }));
    }
  };

  // Função para lidar com alterações de valores nos campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Para campos percentuais, limitar a 2 casas decimais
    if (['entry_fee', 'exit_fee', 'profit_rate', 'daily_withdrawal_rate'].includes(name)) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Limitar a 2 casas decimais e converter para decimal (ex: 0.1% -> 0.001)
        const formattedValue = parseFloat(numValue.toFixed(2)) / 100;
        setSettings(prevSettings => ({
          ...prevSettings,
          [name]: formattedValue
        }));
      }
    } else {
      // Para outros campos, atualizar normalmente
      setSettings(prevSettings => ({
        ...prevSettings,
        [name]: ['working_days', 'operations_per_day', 'simulation_months'].includes(name) 
          ? parseInt(value, 10) || 0 
          : parseFloat(value) || 0
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que o valor inicial não é zero
    if (!settings.initial_amount || settings.initial_amount <= 0) {
      toast.error('O valor inicial deve ser maior que zero');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSaving(false);
        toast.error('Você precisa estar autenticado para salvar configurações.');
        return;
      }
      
      // Adicionar timestamp atual para garantir que a data seja atual
      const currentDate = new Date();
      
      // Verificar os valores antes de enviar
      const settingsToSave = {
        ...settings,
        // Garantir que initial_amount seja maior que zero
        initial_amount: Math.max(0.01, parseFloat(settings.initial_amount) || 0.01),
        // Garantir que percentuais sejam valores decimais válidos (0.1% = 0.001)
        entry_fee: parseFloat(settings.entry_fee) || 0,
        exit_fee: parseFloat(settings.exit_fee) || 0,
        profit_rate: parseFloat(settings.profit_rate) || 0.008,
        daily_withdrawal_rate: parseFloat(settings.daily_withdrawal_rate) || 0,
        // Garantir operações e meses sejam inteiros válidos
        operations_per_day: Math.max(1, parseInt(settings.operations_per_day) || 3),
        simulation_months: Math.max(1, parseInt(settings.simulation_months) || 24),
        // Garantir que dias na semana seja um valor válido (1-7)
        working_days: Math.min(7, Math.max(1, parseInt(settings.working_days) || 5)),
        // Adicionar marcadores de tempo
        timestamp: currentDate.getTime(),
        current_date: currentDate.toISOString()
      };
      
      const response = await axiosInstance.post(apiUrl('/api/investment/settings'), settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Buscar configurações atualizadas
      fetchSettings();
      fetchAllSettings();
      
      toast.success('Cenário salvo com sucesso!');
      
      // Criar simulação logo após salvar configurações
      const createSimulation = window.confirm('Deseja criar uma simulação com esse cenário?');
      if (createSimulation) {
        handleSimulate();
      }
    } catch (error) {
      toast.error('Erro ao salvar cenário. Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleSimulate = async (settingId = null) => {
    // Validar que o valor inicial não é zero
    if (!settingId && (!settings.initial_amount || settings.initial_amount <= 0)) {
      toast.error('O valor inicial deve ser maior que zero');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSaving(false);
        toast.error('Você precisa estar autenticado para realizar simulações.');
        return;
      }
      
      let finalSettingsId = settingId;
      
      // Se não for fornecido um ID, salvar as configurações atuais
      if (!settingId) {
        // Verificar os valores antes de enviar
      const settingsToSend = {
          // Garantir que initial_amount seja maior que zero
          initial_amount: Math.max(0.01, parseFloat(settings.initial_amount) || 0.01),
          // Garantir que percentuais sejam valores decimais válidos (0.1% = 0.001)
          entry_fee: parseFloat(settings.entry_fee) || 0,
          exit_fee: parseFloat(settings.exit_fee) || 0,
          profit_rate: parseFloat(settings.profit_rate) || 0.008,
          daily_withdrawal_rate: parseFloat(settings.daily_withdrawal_rate) || 0,
          // Garantir operações e meses sejam inteiros válidos
          operations_per_day: Math.max(1, parseInt(settings.operations_per_day) || 3),
          simulation_months: Math.max(1, parseInt(settings.simulation_months) || 24),
          // Garantir que dias na semana seja um valor válido (1-7)
          working_days: Math.min(7, Math.max(1, parseInt(settings.working_days) || 5)),
          // Adicionar timestamp atual
          timestamp: new Date().getTime(),
        };
      
      // Primeiro, salvar as configurações
      const settingsResponse = await axiosInstance.post(apiUrl('/api/investment/settings'), settingsToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Tentar obter o ID das configurações de diferentes formas
      if (settingsResponse.data && settingsResponse.data.id) {
          finalSettingsId = settingsResponse.data.id;
      } else if (settingsResponse.data && settingsResponse.data.settings && settingsResponse.data.settings.id) {
          finalSettingsId = settingsResponse.data.settings.id;
      } else {
        throw new Error('Erro ao salvar configurações: ID não encontrado na resposta');
        }
      }
      
      // Depois, criar a simulação com o ID das configurações
      const response = await axiosInstance.post(apiUrl('/api/simulation'), {
        settings_id: finalSettingsId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Tentar obter o ID da simulação de diferentes formas
      let simulationId = null;
      
      if (response.data && response.data.simulation && response.data.simulation.id) {
        simulationId = response.data.simulation.id;
      } else if (response.data && response.data.id) {
        simulationId = response.data.id;
      } else {
        throw new Error('Erro ao criar simulação: ID não encontrado na resposta');
      }
      
      toast.success('Simulação criada com sucesso!');
      window.location.href = `/reports?simulation=${simulationId}`;
    } catch (error) {
      toast.error('Erro ao criar simulação. Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSetting = (settingId) => {
    fetchSettingById(settingId);
  };

  // Funções de formatação
  const formatPercent = (value) => {
    return (value * 100).toFixed(2) + '%';
  };

  const formatCurrency = (value) => {
    return formatCurrencyValue(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'saved') {
      fetchAllSettings();
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectSetting = (settingId) => {
    setSelectedSettings(prev => {
      if (prev.includes(settingId)) {
        return prev.filter(id => id !== settingId);
      } else {
        return [...prev, settingId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredSettings.map(setting => setting.id);
      setSelectedSettings(allIds);
    } else {
      setSelectedSettings([]);
    }
  };

  const handleBulkSimulate = () => {
    if (selectedSettings.length === 0) {
      toast.warning('Selecione pelo menos um cenário para simular');
      return;
    }

    // Perguntar confirmação
    const confirmSimulate = window.confirm(`Deseja criar ${selectedSettings.length} simulações com os cenários selecionados?`);
    if (!confirmSimulate) return;

    // Criar simulações em sequência
    const createSimulations = async () => {
      try {
        setLoading(true);
        for (const settingId of selectedSettings) {
          await handleSimulate(settingId);
          // Pequeno delay para não sobrecarregar o servidor
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        toast.success(`${selectedSettings.length} simulações criadas com sucesso!`);
        navigate('/reports');
      } catch (error) {
        toast.error('Erro ao criar simulações em lote');
      } finally {
        setLoading(false);
      }
    };

    createSimulations();
  };

  // Filtrar configurações salvas com base no termo de pesquisa
  const filteredSettings = savedSettings.filter(setting => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const date = formatDate(setting.created_at).toLowerCase();
    
    return (
      date.includes(searchLower) ||
      setting.initial_amount.toString().includes(searchLower) ||
      setting.profit_rate.toString().includes(searchLower)
    );
  });

  // Variável local para símbolos de moeda
  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    BRL: 'R$'
  };

  if (loading && !savedSettings.length) {
    return (
      <MainLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">CENÁRIOS</h2>
              <div className="text-muted mt-1">
                Gerencie seus cenários de investimento
              </div>
            </div>
            <div className="col-auto ms-auto">
              <div className="btn-list">
                <button 
                  onClick={() => handleTabChange('new')} 
                  className="btn btn-primary d-none d-sm-inline-block"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Novo Cenário
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {fetchError && (
        <div className="container-xl mb-3">
          <div className="alert alert-warning">
            <div className="d-flex">
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" className="icon alert-icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 9v2m0 4v.01"></path>
                  <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
                </svg>
              </div>
              <div>
                <h4 className="alert-title">Aviso</h4>
                <div className="text-muted">
                  Houve um problema ao carregar as configurações: {fetchError}. 
                  Os valores padrão estão sendo exibidos.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="page-body">
        <div className="container-xl">
          {/* Abas/Guias */}
              <div className="card">
                <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'saved' ? 'active' : ''}`}
                    onClick={() => handleTabChange('saved')}
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: activeTab === 'saved' ? theme.colors.successLight : '',
                      color: activeTab === 'saved' ? theme.colors.success : '',
                      borderTop: activeTab === 'saved' ? `3px solid ${theme.colors.success}` : '',
                      fontWeight: activeTab === 'saved' ? 'bold' : 'normal'
                    }}
                  >
                    Cenários Salvos
                    <span className="badge ms-2" style={{ backgroundColor: theme.colors.success, color: 'white' }}>{savedSettings.length}</span>
                  </a>
                </li>
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'new' ? 'active' : ''}`}
                    onClick={() => handleTabChange('new')}
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: activeTab === 'new' ? theme.colors.primaryLight : '',
                      color: activeTab === 'new' ? theme.colors.primary : '',
                      borderTop: activeTab === 'new' ? `3px solid ${theme.colors.primary}` : '',
                      fontWeight: activeTab === 'new' ? 'bold' : 'normal'
                    }}
                  >
                    Novo Cenário
                  </a>
                </li>
              </ul>
            </div>
            
            <div className="card-body">
              {/* Conteúdo da Aba "Cenários Salvos" */}
              {activeTab === 'saved' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex">
                      <div className="me-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Pesquisar cenários..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <button 
                        className="btn btn-success me-2"
                        onClick={handleBulkSimulate}
                        disabled={selectedSettings.length === 0}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M7 4v16l13 -8z"></path>
                        </svg>
                        Simular Selecionados
                      </button>
                      
                      <button className="btn btn-outline-secondary" onClick={fetchAllSettings}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
                          <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
                        </svg>
                        Atualizar
                      </button>
                    </div>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-striped">
                      <thead>
                        <tr>
                          <th width="40">
                            <input 
                              type="checkbox" 
                              className="form-check-input" 
                              checked={selectedSettings.length > 0 && selectedSettings.length === filteredSettings.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>Data de Criação</th>
                          <th>Valor Inicial</th>
                          <th>Taxa de Lucro</th>
                          <th>Dias/Semana</th>
                          <th>Operações/Dia</th>
                          <th>Período</th>
                          <th width="120">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSettings.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <div className="empty">
                                <div className="empty-img">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <circle cx="12" cy="12" r="9"></circle>
                                    <line x1="9" y1="10" x2="9.01" y2="10"></line>
                                    <line x1="15" y1="10" x2="15.01" y2="10"></line>
                                    <path d="M9.5 15.25a3.5 3.5 0 0 1 5 0"></path>
                                  </svg>
                                </div>
                                <p className="empty-title">Nenhum cenário encontrado</p>
                                <p className="empty-subtitle text-muted">
                                  Tente ajustar sua pesquisa ou crie um novo cenário.
                                </p>
                                <div className="empty-action">
                                  <button className="btn btn-primary" onClick={() => handleTabChange('new')}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                      <path d="M12 5l0 14"></path>
                                      <path d="M5 12l14 0"></path>
                                    </svg>
                                    Criar Novo Cenário
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredSettings.map(setting => (
                            <tr key={setting.id}>
                              <td>
                                <input 
                                  type="checkbox" 
                                  className="form-check-input"
                                  checked={selectedSettings.includes(setting.id)}
                                  onChange={() => handleSelectSetting(setting.id)}
                                />
                              </td>
                              <td>{formatDate(setting.created_at)}</td>
                              <td>
                                $ {Number(setting.initial_amount).toFixed(2)}
                              </td>
                              <td className="text-success">{formatPercent(setting.profit_rate)}</td>
                              <td>{setting.working_days || 5}</td>
                              <td>{setting.operations_per_day}</td>
                              <td>{setting.simulation_months} meses</td>
                              <td>
                                <button 
                                  className="btn btn-sm btn-primary me-1"
                                  onClick={() => handleEditSetting(setting.id)}
                                  title="Editar"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                    <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                    <path d="M16 5l3 3"></path>
                                  </svg>
                                </button>
                                <button 
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleSimulate(setting.id)}
                                  title="Simular"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M7 4v16l13 -8z"></path>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Conteúdo da Aba "Novo Cenário" */}
              {activeTab === 'new' && (
                <div className="tab-pane fade show active">
                  <form onSubmit={handleSubmit} className="compact-form">
                    <div className="row equal-height-cards">
                      <div className="col-md-6">
                        <div className="card min-height-card">
                          <div className="card-body">
                            <h5 className="card-title">Parâmetros de Investimento</h5>
                            <div className="d-flex justify-content-end mb-3">
                              <div className="form-group me-2">
                                <label className="form-label short-label">Moeda</label>
                                <select 
                                  className="form-select form-select-sm" 
                                  value={selectedCurrency} 
                                  onChange={(e) => setSelectedCurrency(e.target.value)}
                                >
                                  <option value="USD">USD ($)</option>
                                  <option value="EUR">EUR (€)</option>
                                  <option value="GBP">GBP (£)</option>
                                  <option value="BRL">BRL (R$)</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="form-grid">
                              <div className="mb-2">
                                <label htmlFor="initial_amount" className="form-label short-label">Valor Inicial</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="initial_amount" 
                            name="initial_amount" 
                                    value={settings.initial_amount || ''}
                            onChange={handleChange}
                                    step="0.01"
                                    min="0"
                          />
                                  <span className="input-group-text">{currencySymbols[selectedCurrency]}</span>
                        </div>
                      </div>
                              
                              <div className="mb-2">
                                <label htmlFor="exchange_rate" className="form-label short-label">Taxa Câmbio</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="exchange_rate" 
                            name="exchange_rate" 
                                    value={settings.exchange_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                                    min="0"
                          />
                                  <span className="input-group-text">$</span>
                      </div>
                    </div>
                    
                              <div className="mb-2">
                                <label htmlFor="simulation_months" className="form-label short-label">Período</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="simulation_months" 
                                    name="simulation_months" 
                                    value={settings.simulation_months || ''}
                            onChange={handleChange}
                                    min="1"
                                    max="60"
                          />
                                  <span className="input-group-text">m</span>
                        </div>
                      </div>
                              
                              <div className="mb-2">
                                <label htmlFor="entry_fee" className="form-label short-label">Taxa Entrada</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="entry_fee" 
                                    name="entry_fee" 
                                    value={(settings.entry_fee * 100)?.toFixed(2) || ''}
                            onChange={handleChange}
                                    step="0.01"
                            min="0"
                                    max="10"
                          />
                                  <span className="input-group-text">%</span>
                      </div>
                    </div>
                    
                              <div className="mb-2">
                                <label htmlFor="exit_fee" className="form-label short-label">Taxa Saída</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="exit_fee" 
                                    name="exit_fee" 
                                    value={(settings.exit_fee * 100)?.toFixed(2) || ''}
                            onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    max="10"
                                  />
                                  <span className="input-group-text">%</span>
                        </div>
                      </div>
                              
                              <div className="mb-2">
                                <label htmlFor="working_days" className="form-label short-label">Dias/Semana</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="working_days" 
                                    name="working_days" 
                                    value={settings.working_days || ''}
                            onChange={handleChange}
                                    min="1"
                                    max="7"
                          />
                                  <span className="input-group-text">d</span>
                      </div>
                    </div>
                    
                              <div className="mb-2">
                                <label htmlFor="operations_per_day" className="form-label short-label">Operações/Dia</label>
                                <div className="input-group input-group-sm">
                          <input 
                            type="number" 
                            className="form-control" 
                                    id="operations_per_day" 
                            name="operations_per_day" 
                                    value={settings.operations_per_day || ''}
                            onChange={handleChange}
                            min="1"
                                    max="20"
                                  />
                                  <span className="input-group-text">op</span>
                                </div>
                              </div>
                              
                              <div className="mb-2">
                                <label htmlFor="profit_rate" className="form-label short-label">Taxa Lucro</label>
                                <div className="input-group input-group-sm">
                                  <input 
                                    type="number" 
                                    className="form-control" 
                                    id="profit_rate" 
                                    name="profit_rate" 
                                    value={(settings.profit_rate * 100)?.toFixed(2) || ''}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    max="100"
                                  />
                                  <span className="input-group-text">%</span>
                                </div>
                              </div>
                              
                              <div className="mb-2">
                                <label htmlFor="daily_withdrawal_rate" className="form-label short-label">Taxa Remun.</label>
                                <div className="input-group input-group-sm">
                                  <input 
                                    type="number" 
                                    className="form-control" 
                                    id="daily_withdrawal_rate" 
                                    name="daily_withdrawal_rate" 
                                    value={(settings.daily_withdrawal_rate * 100)?.toFixed(2) || ''}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    max="100"
                                  />
                                  <span className="input-group-text">%</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-3">
                              <button 
                                type="submit" 
                                className="btn btn-primary btn-sm me-2" 
                                disabled={saving}
                              >
                                {saving ? 'Salvando...' : 'Salvar Configuração'}
                              </button>
                              <button 
                                type="button" 
                                className="btn btn-success btn-sm" 
                                onClick={handleSimulate} 
                                disabled={saving}
                              >
                                {saving ? 'Processando...' : 'Simular'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="card min-height-card">
                          <div className="card-body">
                            <h5 className="card-title">Informações</h5>
                            <div className="compact-info-list">
                              <h5 className="text-primary">Como funciona</h5>
                              <p>Configure os parâmetros à esquerda para definir sua estratégia de investimento.</p>
                              <p>Após configurar, você pode salvar ou simular diretamente.</p>
                              
                              <h5 className="text-primary">Parâmetros</h5>
                              <p><strong>Valor Inicial:</strong> Capital inicial para investimento.</p>
                              <p><strong>Taxa Câmbio:</strong> Valor do dólar em relação à moeda local.</p>
                              <p><strong>Período:</strong> Duração da simulação em meses.</p>
                              <p><strong>Taxa Entrada:</strong> Percentual cobrado ao entrar na operação.</p>
                              <p><strong>Taxa Saída:</strong> Percentual cobrado ao sair da operação.</p>
                              <p><strong>Dias/Semana:</strong> Dias úteis de operação por semana.</p>
                              <p><strong>Operações/Dia:</strong> Número de operações realizadas por dia.</p>
                              <p><strong>Taxa Lucro:</strong> Percentual de lucro em cada operação.</p>
                              <p><strong>Taxa Remun.:</strong> Percentual de remuneração diária.</p>
                              
                              <h5 className="text-primary">Simulação</h5>
                              <p>A simulação calcula o rendimento com base nos parâmetros definidos.</p>
                              <p>Os resultados incluem valores acumulados, lucros e taxas.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Setup; 